import React, {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useMemo,
} from "react";
import { Box, Typography } from "@mui/material";

const Label = ({
  labelPosition,
  displayName,
  subtitle,
  description,
  alignment = "auto", // "auto" flips left/right based on x
  offsetLeft = 0,
  maxWidth = 200,
  maxHeight = 200,
  expanded, // controlled by the parent
}) => {
  const measureRef = useRef(null);
  const [collapsedWidth, setCollapsedWidth] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);

  // Measure the natural size of just title+subtitle
  const measure = useCallback(() => {
    const el = measureRef.current;
    if (!el) return;
    setCollapsedWidth(el.scrollWidth);
    setCollapsedHeight(el.scrollHeight);
  }, []);

  // Whenever displayName/subtitle change OR we collapse, re-measure before paint
  useLayoutEffect(() => {
    if (!expanded) {
      measure();
    }
  }, [displayName, subtitle, expanded, measure]);

  // Decide left vs right if in "auto" mode
  const effectiveAlignment = useMemo(() => {
    if (alignment === "auto") {
      const w = typeof window !== "undefined" ? window.innerWidth : 0;
      return labelPosition.x < w / 3 ? "right" : "left";
    }
    return alignment;
  }, [alignment, labelPosition.x]);

  // Map alignment to CSS translate
  const transform = useMemo(() => {
    switch (effectiveAlignment) {
      case "left":
        return "translate(-105%, -50%)";
      case "right":
        return "translate(5%, -50%)";
      case "top":
        return "translate(-50%, -105%)";
      case "bottom":
        return "translate(-50%, 5%)";
      default:
        return "translate(-50%, -50%)";
    }
  }, [effectiveAlignment]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: labelPosition.y,
        left: labelPosition.x + offsetLeft,
        transform,
        backgroundColor: "rgba(0,0,0,0.7)",
        color: "#fff",
        padding: "8px 16px",
        borderRadius: "10px",
        pointerEvents: "none",
        overflow: "hidden",
        display: "inline-block",

        // animate width & height
        transition: "width 0.3s ease, max-height 0.3s ease",
        width: expanded ? `${maxWidth}px` : `${collapsedWidth}px`,
        maxHeight: expanded ? `${maxHeight}px` : `${collapsedHeight}px`,

        textAlign: expanded ? "left" : "center",
        whiteSpace: expanded ? "normal" : "nowrap",
      }}
    >
      {/* only title+subtitle are measured */}
      <Box ref={measureRef}>
        <Typography variant="h5">{displayName}</Typography>
        {!expanded && subtitle && (
          <Typography variant="caption" component="div">
            <em>{subtitle}</em>
          </Typography>
        )}
      </Box>

      {/* subtitle again when expanded */}
      {expanded && subtitle && (
        <Typography variant="caption" component="div">
          <em>{subtitle}</em>
        </Typography>
      )}

      {/* description only when expanded */}
      {expanded && description && (
        <Typography
          variant="body2"
          sx={{ mt: 1, opacity: 1, transition: "opacity 0.3s ease" }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default Label;
